import React, { useState } from "react";

import {
  FaUpload,
  FaFolderOpen,
  FaDatabase,
  FaSignOutAlt,
  FaRobot,
  FaFileAlt,
  FaChartBar,
  FaHome,
  FaLock,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";

import { useAuth } from "react-oidc-context";

import "../styles/Sidebar.css";

import { signOutRedirect } from "../CognitoConfig";
import { getPermissions } from "../utils/permissions";

import ocesaLogo from "../assets/logo_ocesa.png";


const Sidebar = ({
  setView,
  activeView,
  collapsed,
  setCollapsed,
}) => {
  const auth = useAuth();

  const [open, setOpen] = useState(false);

  /*
   * Datos mínimos necesarios del perfil.
   *
   * Por privacidad no mostramos automáticamente el email
   * como fallback visual.
   */
  const profile = auth.user?.profile || {};

  const rawUserName =
    profile["custom:name"] ||
    profile.given_name ||
    profile.name ||
    profile.nickname ||
    profile["cognito:username"] ||
    "Usuario";

  const userName =
    typeof rawUserName === "string" && rawUserName.trim()
      ? rawUserName.trim()
      : "Usuario";

  const userInitial =
    userName.charAt(0).toUpperCase() || "U";


  /*
   * Permisos obtenidos de la identidad autenticada.
   */
  const {
    hasAssignedRole,
    canUpload,
    canViewFiles,
    canViewData,
    canViewReports,
    canViewDashboards,
    canUseBackstageAI,
  } = getPermissions(auth.user);


  const go = (viewKey) => {
    /*
     * La validación final se vuelve a hacer en MainApp
     * mediante canAccessView().
     */
    setView(viewKey);
    setOpen(false);
  };


  const logout = async () => {
    setOpen(false);

    /*
     * Eliminamos primero el estado local OIDC
     * y después ejecutamos el logout de Cognito.
     */
    try {
      await auth.removeUser();
    } finally {
      signOutRedirect();
    }
  };


  const isActive = (viewKey) =>
    activeView === viewKey;


  return (
    <>
      {/* =====================================
          MOBILE BURGER
      ====================================== */}

      <button
        type="button"
        className="burger-btn"
        onClick={() => setOpen((value) => !value)}
        aria-label={open ? "Cerrar menú" : "Abrir menú"}
        aria-expanded={open}
      >
        ☰
      </button>


      {/* =====================================
          MOBILE OVERLAY
      ====================================== */}

      <div
        className={`sidebar-overlay ${open ? "show" : ""}`}
        onClick={() => setOpen(false)}
        aria-hidden="true"
      />


      {/* =====================================
          SIDEBAR
      ====================================== */}

      <aside
        className={[
          "sidebar",
          open ? "open" : "",
          collapsed ? "collapsed" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        aria-label="Navegación principal"
      >
        {/* =====================================
            HEADER / BRAND
        ====================================== */}

        <div className="sidebar-header">
          <div className="sidebar-brand">
            {!collapsed && (
              <img
                src={ocesaLogo}
                alt="OCESA"
                className="sidebar-brand-logo"
                draggable="false"
              />
            )}
          </div>

          <button
            type="button"
            className="sidebar-collapse-btn"
            onClick={() =>
              setCollapsed((value) => !value)
            }
            aria-label={
              collapsed
                ? "Expandir barra lateral"
                : "Contraer barra lateral"
            }
            title={
              collapsed
                ? "Expandir menú"
                : "Contraer menú"
            }
          >
            {collapsed ? (
              <FaChevronRight aria-hidden="true" />
            ) : (
              <FaChevronLeft aria-hidden="true" />
            )}
          </button>
        </div>


        {/* =====================================
            NAVIGATION
        ====================================== */}

        <ul className="sidebar-list">
          <li>
            <button
              type="button"
              className={`sidebar-button ${
                isActive("welcome") ? "active" : ""
              }`}
              onClick={() => go("welcome")}
              title={collapsed ? "Inicio" : undefined}
              aria-current={
                isActive("welcome") ? "page" : undefined
              }
            >
              <FaHome
                className="sidebar-icon"
                aria-hidden="true"
              />

              <span className="sidebar-label">
                Inicio
              </span>
            </button>
          </li>


          {!hasAssignedRole && (
            <li className="sidebar-access-pending">
              <FaLock
                className="sidebar-access-pending-icon"
                aria-hidden="true"
              />

              <div className="sidebar-access-pending-text">
                <strong>Acceso pendiente</strong>

                <span>
                  Tu usuario aún no tiene permisos asignados.
                </span>
              </div>
            </li>
          )}


          {canUpload && (
            <li>
              <button
                type="button"
                className={`sidebar-button ${
                  isActive("upload") ? "active" : ""
                }`}
                onClick={() => go("upload")}
                title={
                  collapsed
                    ? "Subir Archivos"
                    : undefined
                }
                aria-current={
                  isActive("upload") ? "page" : undefined
                }
              >
                <FaUpload
                  className="sidebar-icon"
                  aria-hidden="true"
                />

                <span className="sidebar-label">
                  Subir Archivos
                </span>
              </button>
            </li>
          )}


          {canViewFiles && (
            <li>
              <button
                type="button"
                className={`sidebar-button ${
                  isActive("FileExplorer")
                    ? "active"
                    : ""
                }`}
                onClick={() => go("FileExplorer")}
                title={
                  collapsed
                    ? "Visualizar Archivos"
                    : undefined
                }
                aria-current={
                  isActive("FileExplorer")
                    ? "page"
                    : undefined
                }
              >
                <FaFolderOpen
                  className="sidebar-icon"
                  aria-hidden="true"
                />

                <span className="sidebar-label">
                  Visualizar Archivos
                </span>
              </button>
            </li>
          )}


          {canViewData && (
            <li>
              <button
                type="button"
                className={`sidebar-button ${
                  isActive("data") ? "active" : ""
                }`}
                onClick={() => go("data")}
                title={collapsed ? "Datos" : undefined}
                aria-current={
                  isActive("data") ? "page" : undefined
                }
              >
                <FaDatabase
                  className="sidebar-icon"
                  aria-hidden="true"
                />

                <span className="sidebar-label">
                  Datos
                </span>
              </button>
            </li>
          )}


          {canViewReports && (
            <li>
              <button
                type="button"
                className={`sidebar-button ${
                  isActive("ReportsView")
                    ? "active"
                    : ""
                }`}
                onClick={() => go("ReportsView")}
                title={
                  collapsed
                    ? "Reportes"
                    : undefined
                }
                aria-current={
                  isActive("ReportsView")
                    ? "page"
                    : undefined
                }
              >
                <FaFileAlt
                  className="sidebar-icon"
                  aria-hidden="true"
                />

                <span className="sidebar-label">
                  Reportes
                </span>
              </button>
            </li>
          )}


          {canViewDashboards && (
            <li>
              <button
                type="button"
                className={`sidebar-button ${
                  isActive("DashboardsView")
                    ? "active"
                    : ""
                }`}
                onClick={() => go("DashboardsView")}
                title={
                  collapsed
                    ? "Tableros"
                    : undefined
                }
                aria-current={
                  isActive("DashboardsView")
                    ? "page"
                    : undefined
                }
              >
                <FaChartBar
                  className="sidebar-icon"
                  aria-hidden="true"
                />

                <span className="sidebar-label">
                  Tableros
                </span>
              </button>
            </li>
          )}


          {canUseBackstageAI && (
            <li>
              <button
                type="button"
                className={`sidebar-button ${
                  isActive("chatInterface")
                    ? "active"
                    : ""
                }`}
                onClick={() => go("chatInterface")}
                title={
                  collapsed
                    ? "Backstage AI"
                    : undefined
                }
                aria-current={
                  isActive("chatInterface")
                    ? "page"
                    : undefined
                }
              >
                <FaRobot
                  className="sidebar-icon"
                  aria-hidden="true"
                />

                <span className="sidebar-label">
                  Backstage AI
                </span>
              </button>
            </li>
          )}


          {/* =====================================
              USER / LOGOUT
          ====================================== */}

          <li className="sidebar-footer">
            <div
              className="sidebar-user"
              title={collapsed ? userName : undefined}
            >
              <div
                className="sidebar-user-avatar"
                aria-hidden="true"
              >
                {userInitial}
              </div>

              <div className="sidebar-user-info">
                <span className="sidebar-user-label">
                  Usuario
                </span>

                <strong className="sidebar-user-name">
                  {userName}
                </strong>
              </div>
            </div>

            <button
              type="button"
              className="sidebar-button logout-button"
              onClick={logout}
              title={
                collapsed
                  ? "Cerrar Sesión"
                  : undefined
              }
            >
              <FaSignOutAlt
                className="sidebar-icon"
                aria-hidden="true"
              />

              <span className="sidebar-label">
                Cerrar Sesión
              </span>
            </button>
          </li>
        </ul>
      </aside>
    </>
  );
};


export default Sidebar;
